import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { MeasureField, UnitToggle, type Units } from "./MeasureField";

const ACTIVITY_ADJUSTMENTS = [
  { value: "0", label: "Sedentary (little exercise)", extraMl: 0 },
  { value: "1", label: "Light exercise (< 30 min/day)", extraMl: 350 },
  { value: "2", label: "Moderate exercise (30-60 min/day)", extraMl: 700 },
  { value: "3", label: "Intense exercise (60+ min/day)", extraMl: 1200 },
];

const CLIMATE_ADJUSTMENTS = [
  { value: "temperate", label: "Temperate climate", extraMl: 0 },
  { value: "hot", label: "Hot/humid climate", extraMl: 500 },
];

const TOOL_INFO_CONTENT = {
  what: `Estimates a recommended daily water intake based on your body weight and activity level — a more personalized starting point than the generic "eight glasses a day" advice, which doesn't account for body size or how active someone actually is.`,
  how: `Enter your weight and activity level, and a suggested daily water intake is calculated.`,
  tip: `Climate and individual sweat rate both affect actual hydration needs meaningfully — treat this as a reasonable baseline to adjust upward on hot days or during more intense exercise, rather than a fixed number that applies regardless of conditions.`,
};

export default function WaterIntakeCalculator() {
  const [units, setUnits] = useState<Units>("metric");
  const [weightKg, setWeightKg] = useState<number | null>(70);
  const [activity, setActivity] = useState("1");
  const [climate, setClimate] = useState("temperate");

  const w = weightKg ?? 0;
  const baseMl = w * 33; // ~33ml per kg, a commonly cited baseline
  const activityExtra = ACTIVITY_ADJUSTMENTS.find(a => a.value === activity)?.extraMl ?? 0;
  const climateExtra = CLIMATE_ADJUSTMENTS.find(c => c.value === climate)?.extraMl ?? 0;

  const totalMl = baseMl + activityExtra + climateExtra;
  const totalLiters = totalMl / 1000;
  const totalCups = totalMl / 236.588; // US cups
  const totalOz = totalMl / 29.5735;

  const { blocked } = useLogToolUsage(weightKg != null, 3000);


  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <UnitToggle units={units} onChange={setUnits} />
        <MeasureField label="Body Weight" kind="weight" units={units} initialMetric={70} onValue={setWeightKg} />

        <div>
          <Label>Activity Level</Label>
          <Select value={activity} onValueChange={setActivity}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTIVITY_ADJUSTMENTS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Climate</Label>
          <Select value={climate} onValueChange={setClimate}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CLIMATE_ADJUSTMENTS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="p-4 rounded-xl border bg-primary/5 text-center">
          <p className="text-xs text-muted-foreground">Recommended Daily Water Intake</p>
          <p className="text-2xl font-bold text-primary">{blocked || weightKg == null ? "—" : units === "metric" ? `${totalLiters.toFixed(1)} L` : `${totalOz.toFixed(0)} fl oz`}</p>
        </div>

        {!blocked && weightKg != null && (
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">{units === "metric" ? "Glasses (250 ml)" : "US Cups"}</p><p className="text-sm font-bold" data-testid="wi-second">{units === "metric" ? (totalMl / 250).toFixed(1) : totalCups.toFixed(1)}</p></div>
            <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">{units === "metric" ? "Millilitres (nearest 100)" : "Litres"}</p><p className="text-sm font-bold">{units === "metric" ? (Math.round(totalMl / 100) * 100).toLocaleString("en-US") : totalLiters.toFixed(1)}</p></div>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center">
          General estimate — needs vary with health conditions, pregnancy, and medications. Consult a doctor for personalized guidance.
        </p>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
